import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';
import { SMS_TEMPLATE_PREVIEWS } from '../../../services/smsService';

const SMSNotificationsTab = () => {
  const { profile, organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [settings, setSettings] = useState({
    sms_enabled: false,
    sms_notify_job_status: true,
    sms_notify_new_lead: true,
    sms_notify_invoice_paid: true,
  });
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (organization) {
      setSettings({
        sms_enabled: organization?.sms_enabled || false,
        sms_notify_job_status: organization?.sms_notify_job_status ?? true,
        sms_notify_new_lead: organization?.sms_notify_new_lead ?? true,
        sms_notify_invoice_paid: organization?.sms_notify_invoice_paid ?? true,
      });
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    if (profile?.org_id) {
      fetchLogs();
    }
  }, [profile?.org_id]);

  const fetchLogs = async () => {
    setLogsLoading(true);
    const { data } = await supabase
      ?.from('sms_logs')
      ?.select('*')
      ?.eq('org_id', profile?.org_id)
      ?.order('created_at', { ascending: false })
      ?.limit(20);
    setLogs(data || []);
    setLogsLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('organizations')
        ?.update({
          sms_enabled: settings?.sms_enabled,
          sms_notify_job_status: settings?.sms_notify_job_status,
          sms_notify_new_lead: settings?.sms_notify_new_lead,
          sms_notify_invoice_paid: settings?.sms_notify_invoice_paid,
        })
        ?.eq('id', profile?.org_id);
      if (error) throw error;
      toast?.success('SMS settings saved!');
    } catch (err) {
      toast?.error('Failed to save: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testPhone?.trim()) {
      toast?.error('Enter a phone number to test');
      return;
    }
    setTestSending(true);
    try {
      const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase?.auth?.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          to: testPhone?.trim(),
          message: `Hi there, this is a test notification from ${organization?.name || 'your CRM'}. SMS alerts are active! Reply STOP to opt out. Powered by wilcart.com`,
          org_id: profile?.org_id,
          event_type: 'test',
        }),
      });
      const data = await res?.json();
      if (!res?.ok) {
        toast?.error(data?.error || 'Failed to send test SMS');
      } else {
        toast?.success('Test SMS sent successfully!');
        fetchLogs();
      }
    } catch (err) {
      toast?.error('Error: ' + err?.message);
    } finally {
      setTestSending(false);
    }
  };

  const toggle = (key) => setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));

  const eventTypeLabel = {
    job_status_change: 'Job Status Change',
    new_lead: 'New Lead',
    invoice_paid: 'Invoice Paid',
    test: 'Test',
    unknown: 'Unknown',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enable SMS Toggle */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Icon name="MessageSquare" size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-card-foreground">SMS Notifications</h3>
              <p className="text-sm text-muted-foreground">Send automatic SMS alerts to your clients</p>
            </div>
          </div>
          <button
            onClick={() => toggle('sms_enabled')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
              settings?.sms_enabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.sms_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Notification Events */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-card-foreground mb-1">Notification Events</h3>
        <p className="text-sm text-muted-foreground mb-5">Choose which events trigger an SMS to your clients</p>
        <div className="space-y-4">
          {[
            {
              key: 'sms_notify_job_status',
              icon: 'Briefcase',
              label: 'Job Status Changes',
              desc: 'Notify client when job status changes (scheduled, in progress, completed)',
            },
            {
              key: 'sms_notify_new_lead',
              icon: 'UserPlus',
              label: 'New Lead Confirmation',
              desc: 'Send a confirmation SMS to new leads when they are added',
            },
            {
              key: 'sms_notify_invoice_paid',
              icon: 'DollarSign',
              label: 'Invoice Paid',
              desc: 'Thank client when their invoice payment is recorded',
            },
          ]?.map((item) => (
            <div key={item?.key} className="flex items-center justify-between py-3 border-b border-border last:border-0">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name={item?.icon} size={16} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-card-foreground">{item?.label}</p>
                  <p className="text-xs text-muted-foreground">{item?.desc}</p>
                </div>
              </div>
              <button
                onClick={() => toggle(item?.key)}
                disabled={!settings?.sms_enabled}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 ${
                  settings?.[item?.key] && settings?.sms_enabled ? 'bg-primary' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    settings?.[item?.key] && settings?.sms_enabled ? 'translate-x-4.5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Message Templates — Platform Controlled */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-card-foreground mb-1">Message Templates</h3>
            <p className="text-sm text-muted-foreground">
              Messages are crafted by Wilcart to work across all business types. Your business name is automatically inserted.
            </p>
          </div>
          <span className="flex items-center space-x-1 text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium flex-shrink-0 ml-4">
            <Icon name="Lock" size={11} />
            <span>Platform managed</span>
          </span>
        </div>
        <div className="space-y-3">
          {SMS_TEMPLATE_PREVIEWS?.map((tpl) => (
            <div key={tpl?.event} className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center">
                  <Icon name={tpl?.icon} size={13} className="text-primary" />
                </div>
                <span className="text-xs font-semibold text-card-foreground uppercase tracking-wide">{tpl?.label}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed font-mono bg-background border border-border rounded px-3 py-2">
                {tpl?.preview}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Info" size={13} className="flex-shrink-0" />
          <span>
            Every message includes <strong className="text-foreground">wilcart.com</strong> — subtle branding that helps clients know their notifications are secure and professional.
          </span>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Icon name="Save" size={16} />
          )}
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>

      {/* Test SMS */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-card-foreground mb-1">Test SMS</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Send a test message to verify your Twilio setup is working correctly
        </p>
        <div className="flex space-x-3">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e?.target?.value)}
            placeholder="+1 (555) 000-0000"
            className="flex-1 px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
          />
          <button
            onClick={handleTestSMS}
            disabled={testSending || !testPhone?.trim()}
            className="flex items-center space-x-2 px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-60"
          >
            {testSending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="Send" size={16} />
            )}
            <span>{testSending ? 'Sending...' : 'Send Test'}</span>
          </button>
        </div>
      </div>

      {/* SMS Logs */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-card-foreground">SMS Log</h3>
            <p className="text-sm text-muted-foreground">Recent SMS messages sent from your CRM</p>
          </div>
          <button onClick={fetchLogs} className="text-xs text-primary hover:underline flex items-center space-x-1">
            <Icon name="RefreshCw" size={12} />
            <span>Refresh</span>
          </button>
        </div>
        {logsLoading ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="MessageSquare" size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No SMS messages sent yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs?.map((log) => (
              <div key={log?.id} className="flex items-start justify-between p-3 bg-muted/40 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-0.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      log?.status === 'sent' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {log?.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                    </span>
                    <span className="text-xs text-muted-foreground">{eventTypeLabel?.[log?.event_type] || log?.event_type}</span>
                    <span className="text-xs text-muted-foreground">→ {log?.to_phone}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{log?.message}</p>
                  {log?.error_message && (
                    <p className="text-xs text-red-500 mt-0.5">{log?.error_message}</p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground ml-3 flex-shrink-0">
                  {new Date(log?.created_at)?.toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSNotificationsTab;
